import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Lock, Home, Lightbulb, CalendarDays, CheckSquare, LineChart, Eye, Film } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { apiBase } from "../../components/content/contentUtils";
import ContentOverview from "../../components/content/ContentOverview";
import IdeasBank from "../../components/content/IdeasBank";
import ContentCalendar from "../../components/content/ContentCalendar";
import ContentTracking from "../../components/content/ContentTracking";
import ContentStats from "../../components/content/ContentStats";
import { FieldCommentsProvider } from "../../components/CommentField";
import ContentEditor from "../../components/content/ContentEditor";
import CoachIdeaView from "../../components/content/CoachIdeaView";
import StoryWeek from "../../components/content/StoryWeek";

const TABS = [
  ["apercu", "Vue d'ensemble", Home],
  ["idees", "Banque d'idées", Lightbulb],
  ["calendrier", "Calendrier de contenu", CalendarDays],
  ["stories", "Stories", Film],
  ["tracking", "Tracking", CheckSquare],
  ["stats", "Statistiques", LineChart],
];
const HEAD = {
  idees: ["Banque d'idées", "Note tes idées, retrouve les contenus de ta coach et tes inspirations."],
  calendrier: ["Calendrier de contenu", "Tes posts prévus, jour par jour, avec leur script."],
  stories: ["Stories", "Ta semaine type de stories, jour par jour."],
  tracking: ["Tracking", "Chaque semaine, tes posts publiés, leurs chiffres et le retour de ta coach."],
  stats: ["Statistiques", "Le bilan de tes posts : ce qui a marché, ce qui n'a pas marché."],
};
const err = (e) => toast.error(formatApiError(e?.response?.data?.detail));

function useContentActions(base, setB) {
  const patchItems = (fn) => setB((b) => ({ ...b, items: fn(b.items) }));
  return {
    createItem: async (payload) => {
      try { const { data } = await api.post(`${base}/content-items`, payload); patchItems((l) => [data, ...l]); return data; } catch (e) { err(e); return null; }
    },
    updateItem: async (id, patch) => {
      patchItems((l) => l.map((i) => (i.id === id ? { ...i, ...patch } : i)));
      try {
        const { data } = await api.put(`${base}/content-items/${id}`, patch);
        if ("link" in patch) patchItems((l) => l.map((i) => (i.id === id ? { ...i, transcript_status: data.transcript_status } : i)));
      } catch (e) { err(e); }
    },
    refreshItem: async (id) => {
      try { const { data } = await api.get(`${base}/content-items/${id}`); patchItems((l) => l.map((i) => (i.id === id ? { ...i, transcript: data.transcript, transcript_status: data.transcript_status } : i))); } catch (e) { /* silencieux */ }
    },
    transcribe: async (id) => {
      try { const { data } = await api.post(`${base}/content-items/${id}/transcribe`); patchItems((l) => l.map((i) => (i.id === id ? { ...i, transcript: "", transcript_status: data.transcript_status } : i))); } catch (e) { err(e); }
    },
    deleteItem: async (id) => {
      try { await api.delete(`${base}/content-items/${id}`); patchItems((l) => l.filter((i) => i.id !== id)); toast.success("Contenu supprimé"); } catch (e) { err(e); }
    },
    itemFeedback: async (id, text, notify = true) => {
      try { const { data } = await api.put(`${base}/content-items/${id}/feedback?notify=${notify}`, { text }); patchItems((l) => l.map((i) => (i.id === id ? data : i))); toast.success(notify ? "Retour envoyé ✨" : "Retour enregistré"); return data; } catch (e) { err(e); return null; }
    },
    weekAnalysis: async (week_start, text, kind = "contenus") => {
      try {
        const { data } = await api.put(`${base}/content-weeks/${week_start}/analysis?kind=${kind}`, { text });
        setB((b) => ({ ...b, reports: [data, ...b.reports.filter((r) => r.id !== data.id)] }));
        toast.success("Analyse terminée : ta cliente est prévenue par email ✨");
      } catch (e) { err(e); }
    },
    sendReport: async (week_start, note) => {
      try {
        const { data } = await api.post(`${base}/content-reports`, { week_start, note });
        setB((b) => ({ ...b, reports: [data, ...b.reports.filter((r) => r.id !== data.id)] }));
        toast.success("Tes contenus ont été envoyés à ta coach ✨");
      } catch (e) { err(e); }
    },
    reportFeedback: async (id, text) => {
      try { const { data } = await api.put(`${base}/content-reports/${id}/feedback`, { text }); setB((b) => ({ ...b, reports: b.reports.map((r) => (r.id === id ? data : r)) })); toast.success("Retour envoyé ✨"); } catch (e) { err(e); }
    },
    addInspiration: async (payload) => {
      try { const { data } = await api.post(`${base}/inspirations`, payload); setB((b) => ({ ...b, inspirations: [data, ...b.inspirations] })); toast.success("Inspiration ajoutée ✨"); return true; } catch (e) { err(e); return false; }
    },
    updateInspiration: async (id, patch) => {
      setB((b) => ({ ...b, inspirations: b.inspirations.map((i) => (i.id === id ? { ...i, ...patch } : i)) }));
      try { await api.put(`${base}/inspirations/${id}`, patch); } catch (e) { err(e); }
    },
    upload: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      try { const { data } = await api.post(`${base}/content-upload`, fd); return data.url; } catch (e) { err(e); return null; }
    },
    deleteInspiration: async (id) => {
      try { await api.delete(`${base}/inspirations/${id}`); setB((b) => ({ ...b, inspirations: b.inspirations.filter((i) => i.id !== id) })); } catch (e) { err(e); }
    },
    weekStories: async (week_start, patch) => {
      try {
        const { data } = await api.put(`${base}/content-weeks/${week_start}/stories`, patch);
        setB((b) => ({ ...b, reports: [data, ...b.reports.filter((r) => r.id !== data.id)] }));
        toast.success("Enregistré ✨");
      } catch (e) { err(e); }
    },
    sendStories: async (week_start, note) => {
      try {
        const { data } = await api.post(`/client/content-weeks/${week_start}/stories/send`, { note });
        setB((b) => ({ ...b, reports: [data, ...b.reports.filter((r) => r.id !== data.id)] }));
        toast.success("Tes stories ont été envoyées à ta coach 🎉");
      } catch (e) { err(e); }
    },
    adminNote: async (week_start, kind, note) => {
      try {
        const { data } = await api.put(`${base}/week-note`, { week_start, kind, note });
        setB((b) => ({ ...b, reports: [data, ...b.reports.filter((r) => r.id !== data.id)] }));
        toast.success("Enregistré ✨");
      } catch (e) { err(e); }
    },
  };
}

function Locked() {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl space-y-6">
      <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">Espace de création de contenu</h1>
      <div data-testid="content-locked" className="flex items-center gap-4 rounded-2xl border border-dashed border-bordeaux-border bg-bordeaux-tint/20 p-8 text-bordeaux">
        <Lock size={22} /><p className="font-light">Cet espace sera débloqué par Fanny au bon moment de ton accompagnement ✨</p>
      </div>
    </motion.div>
  );
}

export default function ContentSpace({ adminClientId }) {
  const navigate = useNavigate();
  const isAdmin = !!adminClientId;
  const base = apiBase(adminClientId);
  const [params, setParams] = useSearchParams();
  const tab = params.get("onglet") || "apercu";
  const sub = params.get("sous") || "insp";
  const [b, setB] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const actions = useContentActions(base, setB);
  const navRef = useRef(null);
  useEffect(() => {
    const nav = navRef.current;
    const el = nav && nav.querySelector("[data-active=true]");
    if (el) nav.scrollLeft = el.offsetLeft - (nav.clientWidth - el.clientWidth) / 2;
  }, [tab, b]);

  useEffect(() => {
    api.get(`${base}/content-space`).then(({ data }) => setB({ items: [], reports: [], inspirations: [], ...data })).catch(() => setB({ error: true }));
  }, [base]);

  const goTab = (t, s) => setParams(s ? { onglet: t, sous: s } : { onglet: t });
  if (!b) return <div className="py-10 text-ink-muted">Chargement…</div>;
  if (b.locked && !isAdmin) return <Locked />;
  if (b.error) return <div className="py-10 text-ink-muted">Impossible de charger l'espace.</div>;
  const active = b.items.find((i) => i.id === activeId);
  const common = { items: b.items, isAdmin, actions, onOpen: setActiveId, onTab: goTab };
  const tabs = TABS.filter(([k]) => k !== "stories" || isAdmin || b.story_week?.enabled);
  const [hTitle, hSub] = HEAD[tab] || [`Bienvenue sur ton espace de création de contenu ${b.first_name}`, "Ce que tu as à publier cette semaine et ce que tu dois faire maintenant."];
  const TabIcon = (TABS.find((t) => t[0] === tab) || TABS[0])[2];

  return (
    <FieldCommentsProvider page="content-space" clientId={adminClientId}>
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl space-y-7" data-testid="content-space">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-4">
          <button onClick={() => navigate(isAdmin ? `/admin/clientes/${adminClientId}` : "/espace/hub")} data-testid="content-back" className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1 text-xs text-ink-soft transition-colors hover:text-bordeaux"><ChevronLeft size={13} /> {isAdmin ? "Retour au dossier" : "Revenir à The Elevate Hub"}</button>
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-bordeaux text-white shadow-[0_8px_24px_rgba(123,17,58,0.35)]"><TabIcon size={24} /></span>
            <h1 data-testid="content-title" className="font-sans text-3xl font-medium text-ink sm:text-4xl">{hTitle}</h1>
          </div>
          <p className="text-sm font-light text-ink-soft">{hSub}</p>
        </div>
        {isAdmin && <span data-testid="content-viewas" className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs text-ink-soft"><Eye size={14} className="text-bordeaux" /> Vue de l'espace de {b.first_name}</span>}
      </div>
      <div ref={navRef} className="flex w-full gap-1 overflow-x-auto rounded-full bg-[#2D181E] p-1.5">
        {tabs.map(([k, l, I]) => (
          <button key={k} data-active={tab === k} data-testid={`content-tab-${k}`} title={l} onClick={() => goTab(k)} className={`flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm transition-colors duration-200 ${k === "apercu" ? "" : "flex-1"} ${tab === k ? "bg-bordeaux font-medium text-white shadow-[0_0_20px_rgba(123,17,58,0.6)]" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
            <I size={16} />{k !== "apercu" && <span>{l}</span>}
          </button>
        ))}
      </div>
      <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        {tab === "apercu" && <ContentOverview {...common} reports={b.reports} inspirations={b.inspirations} onTab={goTab} />}
        {tab === "idees" && <IdeasBank {...common} inspirations={b.inspirations} sub={sub} setSub={(s) => goTab("idees", s)} />}
        {tab === "calendrier" && <ContentCalendar {...common} />}
        {tab === "stories" && tabs.some(([k]) => k === "stories") && <StoryWeek data={b.story_week} isAdmin={isAdmin} base={base} onChange={(sw) => setB((x) => ({ ...x, story_week: sw }))} />}
        {tab === "tracking" && <ContentTracking {...common} reports={b.reports} />}
        {tab === "stats" && <ContentStats {...common} />}
      </motion.div>
      {active && (!isAdmin && active.source === "coach" && !active.date
        ? <CoachIdeaView key={active.id} item={active} actions={actions} onClose={() => setActiveId(null)} />
        : <ContentEditor key={active.id} item={active} isAdmin={isAdmin} actions={actions} onClose={() => setActiveId(null)} />)}
    </motion.div>
    </FieldCommentsProvider>
  );
}
